import { useState, useRef, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { resumeApi } from '../lib/api';
import { encryptFile, hashData, getSecretKey } from '../lib/encryption';

// Configure PDF.js worker - use unpkg which has all versions
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Extract text from PDF
async function extractTextFromPdf(arrayBuffer: ArrayBuffer): Promise<string> {
  try {
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const textParts: string[] = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: unknown) => (item as { str: string }).str)
        .join(' ');
      textParts.push(pageText);
    }

    return textParts.join('\n\n');
  } catch (err) {
    console.error('Error extracting PDF text:', err);
    return '';
  }
}

interface ResumeMetadata {
  resumeHash: string;
  resumeSummary: string | null;
  resumeKeywords: string[] | null;
  uploadedAt: string;
  hasFile: boolean;
}

export default function ResumeUpload() {
  const [metadata, setMetadata] = useState<ResumeMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadMetadata();
  }, []);

  async function loadMetadata() {
    try {
      const data = await resumeApi.getMetadata();
      setMetadata(data);
    } catch {
      // No resume yet
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleFileSelect(file: File) {
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB');
      return;
    }

    setError(null);
    setSuccess(null);
    setUploading(true);

    try {
      console.log('Starting resume upload...');

      const secretKey = getSecretKey();
      if (!secretKey) {
        throw new Error('Encryption key not found. Please log in again.');
      }
      console.log('Secret key found');

      // Read file
      console.log('Reading file...');
      const fileData = await file.arrayBuffer();
      console.log('File read, size:', fileData.byteLength);

      // Extract text for AI summarization (runs in parallel)
      console.log('Extracting text from PDF...');
      const textPromise = extractTextFromPdf(fileData);

      // Hash for cache invalidation
      console.log('Generating hash...');
      const fileHash = await hashData(new TextDecoder().decode(fileData.slice(0, 1000)));
      console.log('Hash generated');

      // Encrypt file
      console.log('Encrypting file...');
      const encryptedResume = encryptFile(fileData, secretKey);
      console.log('File encrypted, size:', encryptedResume.length);

      // Wait for text extraction
      const resumeText = await textPromise;
      console.log('Text extracted, length:', resumeText?.length || 0);

      // Upload with text for summarization
      console.log('Uploading to API...');
      const result = await resumeApi.upload(encryptedResume, fileHash, resumeText || undefined);
      console.log('Upload complete:', result);

      setSuccess(
        result.message || 'Resume uploaded successfully!'
      );

      // Reload metadata after a delay to allow summarization to complete
      setTimeout(() => loadMetadata(), 3000);
      await loadMetadata();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload resume');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Are you sure you want to delete your resume?')) return;

    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      await resumeApi.delete();
      setMetadata(null);
      setSuccess('Resume deleted successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete resume');
    } finally {
      setDeleting(false);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner border-primary-600 border-t-transparent h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Resume</h1>
        <p className="mt-1 text-gray-500">
          Upload your resume to enable AI-powered job matching. Your resume is encrypted before upload.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Upload area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleInputChange}
          className="hidden"
        />

        <svg
          className={`mx-auto h-12 w-12 ${dragOver ? 'text-primary-500' : 'text-gray-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>

        <div className="mt-4">
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="spinner border-primary-600 border-t-transparent" />
              <span className="text-sm text-gray-600">Encrypting and uploading...</span>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-900">
                {metadata ? 'Drop a new resume or click to replace' : 'Drop your resume here or click to upload'}
              </p>
              <p className="mt-1 text-xs text-gray-500">PDF files only, up to 10MB</p>
            </>
          )}
        </div>
      </div>

      {/* Current resume info */}
      {metadata && (
        <div className="card">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Current Resume</h2>
              <p className="mt-1 text-sm text-gray-500">
                Uploaded {new Date(metadata.uploadedAt).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="btn btn-danger text-sm"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>

          {/* AI Summary */}
          {metadata.resumeSummary && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700">AI Summary</h3>
              <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
                {metadata.resumeSummary}
              </p>
            </div>
          )}

          {/* Keywords */}
          {metadata.resumeKeywords && metadata.resumeKeywords.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700">Extracted Keywords</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {metadata.resumeKeywords.map((keyword, index) => (
                  <span key={index} className="badge badge-info">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!metadata.resumeSummary && (
            <div className="mt-6 rounded-lg bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">
                Your resume hasn't been processed yet. Run a job search to generate the AI summary and keywords.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Security info */}
      <div className="rounded-lg bg-gray-50 p-6">
        <h3 className="text-sm font-medium text-gray-900">End-to-End Encryption</h3>
        <ul className="mt-3 space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Your resume is encrypted in your browser before upload
          </li>
          <li className="flex items-start gap-2">
            <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Our servers cannot read your resume content
          </li>
          <li className="flex items-start gap-2">
            <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Only you can decrypt your data with your password
          </li>
        </ul>
      </div>
    </div>
  );
}
